import { useEffect, useState } from 'react'

/*
  Privacy Policy page.
  - No Figma design — layout follows the MIG style guide:
      - h1-title page (180px alfarn-2) for the page heading
      - h2-large (48px alfarn-2) for each section heading
      - body-default (24px Poppins light) for body paragraphs
      - 1px black divider between major sections
  - Content sourced from the client-supplied Google Doc
    (Teamwork task 26083932). Static, hardcoded — no CMS data bridge needed.
  - Font sizes are inline per project convention (beats the #app reset
    specificity); class names carry the responsive overrides.
*/

type ContentBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] }
  | {
      type: 'contact'
      company: string
      addressLines: string[]
      email: string
      phone: string
      phoneDisplay: string
    }

interface Section {
  heading: string
  blocks: ContentBlock[]
}

const SECTIONS: Section[] = [
  {
    heading: 'Respecting Your Privacy',
    blocks: [
      {
        type: 'paragraph',
        text: 'Willowcharm Pty Ltd as Trustee of Da Silva Family Trust (ABN 139 390 846) and its related companies (together we, us, our and other similar expressions) are committed to protecting your personal information and complying with privacy laws, which set out standards for the management of personal information. We encourage you to read this policy carefully so that you understand how we deal with that information.',
      },
      {
        type: 'paragraph',
        text: 'We are bound by the Australian Privacy Principles (APPs) as set out in the Privacy Act 1988 (Cth) (Privacy Act) and other applicable laws, such as the Spam Act 2007 (Cth). The APPs are designed to protect the confidentiality of information and the privacy of individuals by regulating the way personal information is collected, stored, used, disclosed, and managed. Personal information is information we hold, which is identifiable as being about you.',
      },
      {
        type: 'paragraph',
        text: 'This policy explains the types of personal information we may collect and hold, how that information is used and with whom the information is shared. It also sets out how you can contact us if you have any queries or concerns about this information.',
      },
    ],
  },
  {
    heading: 'Collection of Personal Information',
    blocks: [
      {
        type: 'paragraph',
        text: 'In order to provide our customers with products and services, we may collect and use personal information about them. If we are not provided with all the personal information we request, we may not be able to supply our products and services to you, and you or your organisation may not be able to participate in future offers of goods or services which we supply. The type of personal information we collect depends upon your relationship with us. When doing business with us, you may need to prove your identity. In some circumstances, the law requires you to do so. We may collect and hold information about you including:',
      },
      {
        type: 'list',
        items: [
          'Contact information and identification such as your name, contact number(s), email address(es), residential and/or business address(es), demographic information (such as postcode, gender);',
          'HR information such as a copy of a passport/driver’s licence and/or electronic fingerprint scan collected as part of employment eligibility verification and timeclock logging;',
          'Payment and billing details (such as credit card, credit information, financial details) and other payment-related information, which we use to invoice you for services and to process payments;',
          'Details regarding conversations we have had with you;',
          'Information collected from marketing campaigns, research, client feedback surveys, your interactions with us including via social media, or publicly available information that you post or publish; and',
          'Other information relevant to you that relates to the Company.',
        ],
      },
      {
        type: 'paragraph',
        text: 'We do not normally collect or store ‘sensitive information’ as defined in the Privacy Act 1988 (Cth) (eg information about ethnic origin, religious beliefs, or health). However, where we are required to do and able to do so, we will ask for your consent before collecting your sensitive information and let you know the purpose at the same time.',
      },
    ],
  },
  {
    heading: 'How Do We Collect Personal Information?',
    blocks: [
      {
        type: 'paragraph',
        text: 'The personal information we collect, and hold is what is reasonably necessary for our business functions and activities. Our collection and holding of personal information occur in a variety of ways, including when you interact with us electronically or in person, when you access our website and portals, and when we provide our services to you.',
      },
      {
        type: 'paragraph',
        text: 'However, we may also collect personal information about you (such as your credit information or credit eligibility information), from someone other than you (such as any other organisation with which you have had dealings or which you have nominated to act as a trade referee, from a credit reporting body or from publicly maintained records).',
      },
      {
        type: 'paragraph',
        text: 'For circumstances where we may receive personal information from third parties, we will protect it as set out in this Privacy Policy.',
      },
    ],
  },
  {
    heading: 'How Do We Store Personal Information?',
    blocks: [
      {
        type: 'paragraph',
        text: 'We take all reasonable steps to keep secure any personal information which we hold about you and to protect your personal information from loss, misuse or unauthorised alteration. Any personal information which we hold about you is stored securely and is limited to our employees who need it to carry out their duties.',
      },
      {
        type: 'paragraph',
        text: 'Our employees are obliged to respect the confidentiality of any personal information held by us.',
      },
      {
        type: 'paragraph',
        text: 'We are not responsible or liable for the security of data sent via the internet.',
      },
    ],
  },
  {
    heading: 'Purpose of Collection',
    blocks: [
      {
        type: 'paragraph',
        text: 'We collect, hold, use and disclose personal information so we can:',
      },
      {
        type: 'list',
        items: [
          'Meet our legal obligations;',
          'Identify our customers, potential customers and their representatives;',
          'Provide our products and services or other benefits to you;',
          'Communicate with you;',
          'Inform you of any initiatives we think may be of interest to you;',
          'Inform you about our products and services and the benefits of using our products and services;',
          'Provide you with information about offers or other benefits that may become available;',
          'Seek your opinion or comments about our products and services;',
          'Carry out billing and debt recovery activities;',
          'Carry out our management, administrative, quality assurance and complaint handling activities;',
          'Develop and implement initiatives to improve our products and services; and for any other secondary purposes.',
        ],
      },
    ],
  },
  {
    heading: 'Use and Disclosure of Personal Information',
    blocks: [
      {
        type: 'paragraph',
        text: 'Personal information provided to us may be shared with any of our employees, insurers, professional advisors, agents, suppliers or subcontractors, insofar as reasonably necessary for the purposes set out in this Policy. Personal information is only supplied to a third party when it is required for the delivery of our services. We take all reasonably practical steps to keep such information strictly confidential.',
      },
      {
        type: 'paragraph',
        text: 'Information may be provided to third parties where any functions or services relating to the purpose for which the personal information is collected are being outsourced or you would reasonably expect us to disclose it to a third party for a particular purpose.',
      },
      {
        type: 'paragraph',
        text: 'Except as indicated above, we will not disclose your personal information to a third party unless:',
      },
      {
        type: 'list',
        items: [
          'you have consented to the disclosure;',
          'the third party is our service provider or contractor, in which case we will require them to use and disclose the personal information only for the purpose for which it was provided to them;',
          'the third party is a person involved in a dealing or proposed dealing (including a sale) of all or part of our assets and business;',
          'the third party is a credit reporting agency, your creditor, banker, financier, credit provider, mortgage insurer, or trade insurer;',
          'the disclosure is to a related entity; or',
          'the disclosure is permitted, required or authorised by or under law.',
        ],
      },
    ],
  },
  {
    heading: 'Marketing',
    blocks: [
      {
        type: 'paragraph',
        text: 'From time to time, we may use your personal information to contact you by mail, SMS, telephone, email or online to inform you about new services, offerings, surveys, promotions and newsletters. In so far as the law allows us to, we are likely to market to you unless you tell us otherwise. At the same time, we will give you the opportunity to opt-out of direct marketing communications. If you do not want to be contacted for marketing purposes, you can also advise by contacting us through one of the methods set out below. Upon receipt of notice, we will take immediate steps to ensure that you do not receive any such direct marketing information in future.',
      },
    ],
  },
  {
    heading: 'Cookie Collection and Usage',
    blocks: [
      {
        type: 'paragraph',
        text: 'We may use cookies on our website or apply other automated means, including server logs or proxy logs, to track site visitor activity and collect site data. Cookies are unique identification numbers like tags that are placed on the browser of our website users. These cookies are used to retain login and location information in order to make your experience more convenient and personal. We do not use cookies to track your internet activity before or after you leave our website. No other business has access to our cookies.',
      },
    ],
  },
  {
    heading: 'Communications Quality and Access to Personal Information',
    blocks: [
      {
        type: 'paragraph',
        text: 'We take all reasonable precautions to ensure that the personal information collected, used and disclosed is accurate, complete and up-to-date. The accuracy of the information is, however, dependent to a large extent on the information with which we are provided. We strongly recommend that you:',
      },
      {
        type: 'list',
        items: [
          'Advise us if there are errors in your personal information at any time;',
          'Keep us up to date with changes to your personal information.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Wherever possible, we will correct any error on request. In some cases, we may ask for a written request for correction.',
      },
      {
        type: 'paragraph',
        text: 'Individuals have a right to access their own personal information in a record that is in the custody or under the control of us. However, where the access:',
      },
      {
        type: 'list',
        items: [
          'Impacts on the privacy of others;',
          'The request for access is frivolous or vexatious;',
          'There are existing or anticipated legal proceedings; or',
          'The access can be denied under law or by a law enforcement agency;',
        ],
      },
      {
        type: 'paragraph',
        text: 'We may deny your request for access. If we refuse a request in whole or in part, we will provide the reasons for the refusal. In some cases where exceptions to access apply, we may withhold that information and provide you with the remainder of the record.',
      },
      {
        type: 'paragraph',
        text: 'You may request access to your personal information by writing to us (contact details below). You must provide sufficient information in your request to allow us to identify the information you are seeking.',
      },
      {
        type: 'paragraph',
        text: 'We may also charge a fee to cover the reasonable costs we incur in processing your request.',
      },
    ],
  },
  {
    heading: 'Security of Personal Information',
    blocks: [
      {
        type: 'paragraph',
        text: 'We protect personal information in a manner appropriate for the sensitivity of the information. We make reasonable effort to prevent any loss, misuse, disclosure or modification of personal information.',
      },
      {
        type: 'paragraph',
        text: 'The security of personal information is important to us, however, no method of transmission over the Internet, or method of electronic storage, is absolutely secure. Whilst we attempt to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.',
      },
      {
        type: 'paragraph',
        text: 'Your personal information is stored behind the enterprise firewall and daily backups of personal information is conducted. Additionally, off-site backups of this information are maintained.',
      },
      {
        type: 'paragraph',
        text: 'We use appropriate security measures when destroying personal information, including shredding paper documents, records, and permanently deleting electronic records on their statutory expiration.',
      },
      {
        type: 'paragraph',
        text: 'We retain personal information as long as reasonably necessary to fulfil the purposes for which the information was collected or for legal or business purposes.',
      },
      {
        type: 'paragraph',
        text: 'You are solely responsible for maintaining the secrecy of any passwords and other account information pertaining to our platform, apps or services.',
      },
    ],
  },
  {
    heading: 'Changes to This Privacy Policy',
    blocks: [
      {
        type: 'paragraph',
        text: 'This Privacy Policy is current at the date below. We may, without notice, change this Policy periodically or update this Policy as required. It is your responsibility to check our website and make sure you keep up to date with any changes to this policy.',
      },
    ],
  },
  {
    heading: 'Complaints',
    blocks: [
      {
        type: 'paragraph',
        text: 'We are committed to constantly improving our procedures so that your personal information is treated appropriately. If you feel that we have failed to deal with your personal information in accordance with this policy, or the APPs, please contact us at the contact details listed below so we have an opportunity to resolve the issue to your satisfaction.',
      },
      {
        type: 'paragraph',
        text: 'Our privacy officer will:',
      },
      {
        type: 'list',
        items: [
          'Listen to your concerns and grievances;',
          'Discuss with you the ways in which we can remedy the situation; and',
          'Put in place an action plan to resolve your complaint and improve our information handling procedures if appropriate.',
        ],
      },
    ],
  },
  {
    heading: 'Contact Us',
    blocks: [
      {
        type: 'paragraph',
        text: 'If you have any questions about our Privacy Policy, or any issues or complaints about how we have collected, used, stored, handled and/or disclosed your Personal Information, please contact our Head Office at:',
      },
      {
        type: 'contact',
        company: 'Willowcharm Pty Ltd',
        addressLines: ['Unit 1, 174-180 Pacific Highway', 'North Sydney NSW 2060'],
        email: 'esag.sydney@gmail.com',
        phone: '+61434536772',
        phoneDisplay: '0434 536 772',
      },
      {
        type: 'paragraph',
        text: 'We will aim to respond to your concerns as quickly as possible.',
      },
    ],
  },
]

/* ---- Designed contact card: company, address, clickable email + phone ---- */
function ContactCard({
  company,
  addressLines,
  email,
  phone,
  phoneDisplay,
}: {
  company: string
  addressLines: string[]
  email: string
  phone: string
  phoneDisplay: string
}) {
  return (
    <div className="mig-privacy-contact-card">
      <div className="mig-privacy-contact-grid">
        <div className="mig-privacy-contact-address">
          <h3 className="mig-privacy-contact-company">{company}</h3>
          <address className="mig-privacy-contact-lines">
            {addressLines.map((line, i) => (
              <span key={i}>{line}</span>
            ))}
          </address>
        </div>

        <div className="mig-privacy-contact-channels">
          <div className="mig-privacy-contact-channel">
            <span className="mig-privacy-contact-label">Email</span>
            <a href={`mailto:${email}`} className="mig-privacy-contact-link">{email}</a>
          </div>

          <div className="mig-privacy-contact-channel">
            <span className="mig-privacy-contact-label">Telephone</span>
            <a href={`tel:${phone}`} className="mig-privacy-contact-link">{phoneDisplay}</a>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ---- Render a single content block ---- */
function Block({ block }: { block: ContentBlock }) {
  if (block.type === 'paragraph') {
    return <p className="mig-privacy-paragraph">{block.text}</p>
  }
  if (block.type === 'list') {
    return (
      <ul className="mig-privacy-list">
        {block.items.map((item, i) => (
          <li key={i} className="mig-privacy-list-item">{item}</li>
        ))}
      </ul>
    )
  }
  // contact block
  return (
    <ContactCard
      company={block.company}
      addressLines={block.addressLines}
      email={block.email}
      phone={block.phone}
      phoneDisplay={block.phoneDisplay}
    />
  )
}

export default function PrivacyPolicy() {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  return (
    <section className="mig-page-section">
      <div className="mig-page-title-row" data-visible={visible}>
        <h1 className="mig-privacy-page-title">Privacy Policy</h1>
      </div>

      <div className="mig-privacy-content">
        {SECTIONS.map((section, i) => (
          <article key={section.heading} className="mig-privacy-section">
            <h2 className="mig-privacy-section-heading">{section.heading}</h2>

            <div className="mig-privacy-section-body">
              {section.blocks.map((block, idx) => (
                <Block key={idx} block={block} />
              ))}
            </div>

            {i < SECTIONS.length - 1 && (
              <div className="mig-privacy-section-divider">
                <div className="mig-divider-line" />
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  )
}
